/**
 * Live vehicle map. Renders only when a Google Maps browser key is configured
 * and the job has a vehicle location; otherwise the parent omits it. The marker
 * moves as the polled location updates — the map object is created once and
 * re-centred, so it doesn't flicker on each poll.
 */

import { Loader } from '@googlemaps/js-api-loader';
import { useEffect, useRef } from 'react';
import { GOOGLE_MAPS_KEY } from './config';
import type { VehicleLocation, PublicJobLocation } from './api';

let loaderPromise: Promise<typeof google> | null = null;
function loadMaps(): Promise<typeof google> {
  if (!loaderPromise) {
    loaderPromise = new Loader({ apiKey: GOOGLE_MAPS_KEY, version: 'weekly' }).load();
  }
  return loaderPromise;
}

export function VehicleMap({ vehicle, destination, onError }: {
  vehicle: VehicleLocation;
  destination?: PublicJobLocation | null;
  onError?: () => void;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const vehicleMarker = useRef<google.maps.Marker | null>(null);
  const destMarker = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    // Google invokes this global on any auth failure (invalid key, referrer not
    // allowed). Bubble up so the parent can hide the map cleanly.
    (window as unknown as { gm_authFailure?: () => void }).gm_authFailure = () => onError?.();
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    loadMaps().then((g) => {
      if (cancelled || !divRef.current) return;
      const pos = { lat: vehicle.latitude, lng: vehicle.longitude };
      if (!mapRef.current) {
        mapRef.current = new g.maps.Map(divRef.current, {
          center: pos,
          zoom: 14,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
        });
      }
      const map = mapRef.current;

      if (!vehicleMarker.current) {
        vehicleMarker.current = new g.maps.Marker({
          map,
          title: 'Driver',
          icon: {
            path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 5,
            fillColor: '#FF8A6B',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            rotation: vehicle.heading ?? 0,
          },
        });
      }
      vehicleMarker.current.setPosition(pos);
      vehicleMarker.current.setIcon({
        path: g.maps.SymbolPath.FORWARD_CLOSED_ARROW,
        scale: 5,
        fillColor: '#FF8A6B',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
        rotation: vehicle.heading ?? 0,
      });
      map.setCenter(pos);

      if (destination?.latitude != null && destination?.longitude != null && !destMarker.current) {
        destMarker.current = new g.maps.Marker({
          map,
          position: { lat: destination.latitude, lng: destination.longitude },
          title: 'Destination',
        });
      }
    }).catch(() => { if (!cancelled) onError?.(); });
    return () => { cancelled = true; };
  }, [vehicle, destination, onError]);

  return <div ref={divRef} style={{ width: '100%', height: 260, borderRadius: 14 }} aria-label="Live driver location" />;
}
